(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const shopRoot = () => window.Shopify?.routes?.root || "/";

  function initScrollFilm(root) {
    if (!root || root.dataset.hcReady === "true") return;
    const video = root.querySelector(".hc-hero__video");
    const poster = root.querySelector(".hc-hero__poster");
    const progress = root.querySelector(".hc-hero__progress span");
    if (!video) return;

    root.dataset.hcReady = "true";
    const mobileQuery = window.matchMedia("(max-width: 860px)");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let duration = 0;
    let raf = 0;
    let targetTime = 0;
    let source = "";

    const chooseSource = () => mobileQuery.matches ? root.dataset.mobileVideo : root.dataset.desktopVideo;
    const loadSource = () => {
      const next = chooseSource();
      if (!next || next === source) return;
      source = next;
      video.src = source;
      video.load();
    };
    const measure = () => {
      const rect = root.getBoundingClientRect();
      const passed = clamp(-rect.top / Math.max(1, root.offsetHeight - window.innerHeight));
      root.style.setProperty("--hc-film-progress", passed.toFixed(4));
      if (progress) progress.style.transform = `scaleX(${passed})`;
      targetTime = passed * Math.max(0, duration - 0.05);
    };
    const draw = () => {
      raf = 0;
      if (!duration || reduceMotion) return;
      if (Math.abs(video.currentTime - targetTime) > 0.025) {
        try { video.currentTime = targetTime; } catch (_) {}
      }
    };
    const requestDraw = () => {
      measure();
      if (!raf) raf = window.requestAnimationFrame(draw);
    };

    video.addEventListener("loadedmetadata", () => {
      duration = Number.isFinite(video.duration) ? video.duration : 0;
      requestDraw();
    });
    video.addEventListener("loadeddata", () => {
      poster?.classList.add("is-hidden");
      requestDraw();
    });
    const primeVideo = () => {
      const promise = video.play();
      if (promise?.then) promise.then(() => video.pause()).catch(() => {});
    };
    window.addEventListener("scroll", requestDraw, { passive: true });
    window.addEventListener("resize", requestDraw, { passive: true });
    window.addEventListener("pointerdown", primeVideo, { passive: true, once: true });
    mobileQuery.addEventListener?.("change", () => {
      source = "";
      poster?.classList.remove("is-hidden");
      loadSource();
    });
    loadSource();
    requestDraw();
  }

  function activateMedia(gallery, mediaId) {
    if (!gallery || !mediaId) return;
    gallery.querySelectorAll("[data-media-id]").forEach((media) => {
      const active = String(media.dataset.mediaId) === String(mediaId);
      media.classList.toggle("is-active", active);
      const video = media.querySelector("video");
      if (video) {
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        if (active) video.play().catch(() => {});
        else video.pause();
      }
    });
    gallery.querySelectorAll("[data-media-target]").forEach((thumb) => {
      const active = String(thumb.dataset.mediaTarget) === String(mediaId);
      thumb.classList.toggle("is-active", active);
      thumb.setAttribute("aria-current", active ? "true" : "false");
    });
  }

  function initGallery(gallery) {
    if (!gallery || gallery.dataset.hcReady === "true") return;
    gallery.dataset.hcReady = "true";
    const initialMedia = gallery.querySelector("[data-media-id].is-active");
    if (initialMedia) activateMedia(gallery, initialMedia.dataset.mediaId);
    gallery.addEventListener("click", (event) => {
      const button = event.target.closest("[data-media-target]");
      if (button) activateMedia(gallery, button.dataset.mediaTarget);
    });
  }

  function parseOptions(option) {
    try { return JSON.parse(option.dataset.options || "[]"); }
    catch (_) { return []; }
  }

  function initProduct(root) {
    if (!root || root.dataset.hcReady === "true") return;
    root.dataset.hcReady = "true";
    const form = root.querySelector("[data-product-form]");
    const variantSelect = root.querySelector("[data-variant-select]");
    const optionSelects = [...root.querySelectorAll("[data-product-option]")];
    const gallery = root.closest(".hc-product-shell")?.querySelector("[data-product-gallery]");
    if (!form || !variantSelect) return;

    const updateVariant = () => {
      const selectedValues = optionSelects.map((select) => select.value);
      const variantOption = [...variantSelect.options].find((option) => {
        const values = parseOptions(option);
        return values.length === selectedValues.length && values.every((value, index) => String(value) === String(selectedValues[index]));
      }) || variantSelect.options[variantSelect.selectedIndex];
      if (!variantOption) return;

      variantSelect.value = variantOption.value;
      const available = variantOption.dataset.available === "true";
      const price = root.querySelector("[data-product-price]");
      const compare = root.querySelector("[data-compare-price]");
      const stock = root.querySelector("[data-stock-status]");
      const button = root.querySelector("[data-add-to-cart]");
      const label = button?.querySelector("[data-add-label]");

      if (price) price.textContent = variantOption.dataset.price || "";
      if (compare) compare.textContent = variantOption.dataset.compare || "";
      if (stock) stock.innerHTML = available ? `<span></span>${stock.dataset.inStock}` : stock.dataset.soldOut;
      if (button) button.disabled = !available;
      if (label && button) label.textContent = available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
      if (variantOption.dataset.mediaId) activateMedia(gallery, variantOption.dataset.mediaId);

      if (window.location.pathname.includes("/products/")) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variantOption.value);
        window.history.replaceState({}, "", url);
      }
    };

    optionSelects.forEach((select) => select.addEventListener("change", updateVariant));
    form.addEventListener("submit", async (event) => {
      const submitter = event.submitter;
      if (!submitter?.matches("[data-add-to-cart]")) return;
      event.preventDefault();
      const label = submitter.querySelector("[data-add-label]");
      const defaultLabel = label?.textContent;
      submitter.disabled = true;
      if (label) label.textContent = "Ajout en cours…";
      try {
        const response = await fetch(`${shopRoot()}cart/add.js`, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error("Cart add failed");
        await refreshCartDrawer();
        openCartDrawer();
      } catch (_) {
        form.submit();
      } finally {
        submitter.disabled = false;
        if (label && defaultLabel) label.textContent = defaultLabel;
      }
    });
    updateVariant();
  }

  function openCartDrawer() {
    const drawer = document.querySelector("[data-cart-drawer]");
    if (!drawer) return;
    drawer.setAttribute("aria-hidden", "false");
    document.body.classList.add("hc-cart-is-open");
    document.querySelectorAll("[data-cart-open]").forEach((button) => button.setAttribute("aria-expanded", "true"));
    window.setTimeout(() => drawer.querySelector(".hc-cart-drawer__panel")?.focus(), 50);
  }

  function closeCartDrawer() {
    const drawer = document.querySelector("[data-cart-drawer]");
    if (!drawer) return;
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("hc-cart-is-open");
    document.querySelectorAll("[data-cart-open]").forEach((button) => button.setAttribute("aria-expanded", "false"));
  }

  async function refreshCartDrawer() {
    const [sectionsResponse, cartResponse] = await Promise.all([
      fetch(`${shopRoot()}?sections=headcarer-cart-drawer`),
      fetch(`${shopRoot()}cart.js`, { headers: { Accept: "application/json" } })
    ]);
    const sections = await sectionsResponse.json();
    const cart = await cartResponse.json();
    const html = sections["headcarer-cart-drawer"];
    const current = document.getElementById("shopify-section-headcarer-cart-drawer");
    if (html && current) {
      const template = document.createElement("template");
      template.innerHTML = html.trim();
      const replacement = template.content.firstElementChild;
      if (replacement) current.replaceWith(replacement);
    }
    document.querySelectorAll(".hc-cart-count").forEach((count) => { count.textContent = cart.item_count; });
    init(document);
  }

  function init(scope = document) {
    scope.querySelectorAll("[data-hc-scroll-film]").forEach(initScrollFilm);
    scope.querySelectorAll("[data-product-gallery]").forEach(initGallery);
    scope.querySelectorAll("[data-product-root]").forEach(initProduct);
    document.documentElement.classList.remove("no-js");
  }

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-cart-open]")) openCartDrawer();
    if (event.target.closest("[data-cart-close]")) closeCartDrawer();
  });
  document.addEventListener("keydown", (event) => { if (event.key === "Escape") closeCartDrawer(); });
  document.addEventListener("shopify:section:load", (event) => init(event.target));

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
})();
